import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { businessInfo, faqs } from '../mockData';
import { siteConfig, residentialFaqs } from '../content/siteContent';
import usePageMeta from '../hooks/usePageMeta';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const allFaqs = [...residentialFaqs, ...faqs];

const Contact = () => {
  usePageMeta({
    title: 'Contact Us | Shore Doors Locksmiths',
    description:
      'Call Shore Doors Locksmiths on 09 972 4640 or send an enquiry — residential locksmith work, commercial door hardware, access control and trade partnerships.',
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    projectType: '',
    message: '',
    honeypot: '' // Hidden field for spam protection
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // No API base configured at build time — fail fast to direct contact rather
    // than posting into the void (REACT_APP_BACKEND_URL must be set in Render).
    if (!API_URL) {
      setSubmitStatus('error');
      toast.error(
        `Online enquiries are briefly unavailable — please email ${businessInfo.email} or call ${businessInfo.phone}.`
      );
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          projectType: formData.projectType,
          message: formData.message,
          pageSource: 'contact',
          honeypot: formData.honeypot
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setSubmitStatus('success');
        toast.success(data.message || "Thanks, we'll be in touch shortly.");
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          projectType: '',
          message: '',
          honeypot: ''
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        setSubmitStatus('error');
        
        if (response.status === 429) {
          toast.error('Too many requests. Please wait a minute before trying again.');
        } else if (response.status === 503) {
          toast.error(
            `Email service temporarily unavailable. Please contact us directly at ${businessInfo.email} or call ${businessInfo.phone}.`
          );
        } else {
          toast.error(errorData.detail || 'Something went wrong. Please try again.');
        }
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
      toast.error(
        `Unable to submit form. Please contact us directly at ${businessInfo.email} or call ${businessInfo.phone}.`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gray-950 py-16 sm:py-20 lg:py-24 text-white overflow-hidden">
        <div className="absolute inset-y-0 right-0 w-full md:w-3/5" aria-hidden="true">
          <img src="/images/stock/contact-van-phone.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-950 via-gray-950/50 to-gray-950/5"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Get in Touch
            </h1>
            <p className="text-xl sm:text-2xl text-gray-300">
              Home, business or project site — call us or send an enquiry and we'll sort it
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="border-gray-200">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="text-blue-600" size={24} />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Phone</h3>
                <a href={`tel:${businessInfo.phoneTel}`} className="text-blue-600 hover:text-blue-700">
                  {businessInfo.phone}
                </a>
                <p className="text-sm text-gray-600 mt-1">
                  or <a href={`tel:${businessInfo.mobileTel}`} className="text-blue-600 hover:text-blue-700">{businessInfo.mobile}</a> (mobile)
                </p>
              </CardContent>
            </Card>

            <Card className="border-gray-200">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="text-blue-600" size={24} />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Email</h3>
                <a href={`mailto:${businessInfo.email}`} className="text-blue-600 hover:text-blue-700 break-all">
                  {businessInfo.email}
                </a>
              </CardContent>
            </Card>

            <Card className="border-gray-200">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="text-blue-600" size={24} />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Location</h3>
                <p className="text-gray-600">{businessInfo.address}</p>
              </CardContent>
            </Card>

            <Card className="border-gray-200">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="text-blue-600" size={24} />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Hours</h3>
                <p className="text-gray-600">{siteConfig.hoursLine}</p>
                <p className="text-sm text-blue-600 font-semibold">{siteConfig.afterHoursLine}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Discuss Your Project
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Homeowner, business, property manager or trade partner — fill out the form below
                and we'll be in touch within 24 hours. If it's urgent, call us.
              </p>

              <Card className="border-gray-200">
                <CardContent className="p-6 lg:p-8">
                  {submitStatus === 'success' ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="text-green-600" size={32} />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">Thanks for your enquiry!</h3>
                      <p className="text-gray-600 mb-6">We'll be in touch shortly.</p>
                      <Button 
                        onClick={() => setSubmitStatus(null)}
                        variant="outline"
                        className="border-blue-600 text-blue-600 hover:bg-blue-50"
                      >
                        Submit Another Enquiry
                      </Button>
                    </div>
                  ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    {/* Honeypot field - hidden from users, traps bots */}
                    <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
                      <Input
                        type="text"
                        name="honeypot"
                        value={formData.honeypot}
                        onChange={handleChange}
                        tabIndex="-1"
                        autoComplete="off"
                      />
                    </div>

                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Smith"
                        required
                        className="mt-2"
                        data-testid="contact-name-input"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@company.co.nz"
                        required
                        className="mt-2"
                        data-testid="contact-email-input"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="022 123 4567"
                        className="mt-2"
                        data-testid="contact-phone-input"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <Label htmlFor="company">Company/Organization</Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your Company Name"
                        className="mt-2"
                        data-testid="contact-company-input"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <Label htmlFor="projectType">Project Type</Label>
                      <select
                        id="projectType"
                        name="projectType"
                        value={formData.projectType}
                        onChange={handleChange}
                        className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="contact-project-type-select"
                        disabled={isSubmitting}
                      >
                        <option value="">Select enquiry type</option>
                        <option value="Residential Locksmith">Residential — locks, rekeying, security upgrades</option>
                        <option value="Commercial Locksmith">Commercial locksmith work</option>
                        <option value="Door Hardware Installation">Commercial door hardware installation</option>
                        <option value="Access Control Installation">Access control installation</option>
                        <option value="Master Key Systems">Master key / restricted key systems</option>
                        <option value="Maintenance & IQP">Maintenance & IQP support</option>
                        <option value="Trade Partner Enquiry">Trade partner enquiry (integrators & builders)</option>
                        <option value="Other">Other / Not sure</option>
                      </select>
                    </div>

                    <div>
                      <Label htmlFor="message">Project Details *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your project: facility type, number of access points, timeline, special requirements..."
                        required
                        rows={5}
                        className="mt-2"
                        data-testid="contact-message-input"
                        disabled={isSubmitting}
                      />
                    </div>

                    {submitStatus === 'error' && (
                      <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                        <div>
                          <p className="text-red-800 font-medium">Unable to send your message</p>
                          <p className="text-red-700 text-sm mt-1">
                            Please contact us directly at{' '}
                            <a href={`mailto:${businessInfo.email}`} className="underline">{businessInfo.email}</a>
                            {' '}or call{' '}
                            <a href={`tel:${businessInfo.phoneTel}`} className="underline">{businessInfo.phone}</a>
                          </p>
                        </div>
                      </div>
                    )}

                    <Button 
                      type="submit" 
                      size="lg" 
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold disabled:opacity-50"
                      data-testid="contact-submit-button"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 animate-spin" size={20} />
                          Sending...
                        </>
                      ) : (
                        <>
                          Submit Enquiry
                          <Send className="ml-2" size={20} />
                        </>
                      )}
                    </Button>

                    <p className="text-sm text-gray-600 text-center">
                      By submitting this form, you agree to be contacted by Shore Doors regarding your project enquiry.
                    </p>
                  </form>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Additional Info */}
            <div className="space-y-8">
              {/* Why Contact Us */}
              <Card className="border-gray-200">
                <CardContent className="p-6 lg:p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    What Happens Next?
                  </h3>
                  <div className="space-y-4">
                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-600 font-bold">1</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Initial Contact</h4>
                        <p className="text-gray-600">
                          We'll reach out within 24 hours to discuss your project scope and installation requirements.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-600 font-bold">2</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Site Review</h4>
                        <p className="text-gray-600">
                          We review the site and project specifications with your integrator or project team.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-600 font-bold">3</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Installation Quote</h4>
                        <p className="text-gray-600">
                          Receive a clear quote covering installation scope, timelines, and hardware requirements.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-600 font-bold">4</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Delivery</h4>
                        <p className="text-gray-600">
                          We install to your programme, coordinating with your integrator and other trades on site.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Emergency Contact */}
              <Card className="border-gray-200 bg-blue-50">
                <CardContent className="p-6 lg:p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Need help today?
                  </h3>
                  <p className="text-gray-700 mb-4">
                    Call us during business hours ({siteConfig.hoursLine}) and we'll get to you as
                    fast as we can. Overnight call-outs are coming as the team grows.
                  </p>
                  <a href={`tel:${businessInfo.phoneTel}`}>
                    <Button size="lg" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                      <Phone className="mr-2" size={20} />
                      Call {businessInfo.phone}
                    </Button>
                  </a>
                </CardContent>
              </Card>

              {/* Service Area */}
              <Card className="border-gray-200">
                <CardContent className="p-6 lg:p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Service Area
                  </h3>
                  <p className="text-gray-700 mb-4">
                    Residential locksmith work centres on the North Shore; commercial and trade
                    work runs throughout the Auckland region, including:
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="text-green-600" size={16} />
                      <span className="text-gray-700">Auckland CBD</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="text-green-600" size={16} />
                      <span className="text-gray-700">North Shore</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="text-green-600" size={16} />
                      <span className="text-gray-700">West Auckland</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="text-green-600" size={16} />
                      <span className="text-gray-700">South Auckland</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="text-green-600" size={16} />
                      <span className="text-gray-700">East Auckland</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="text-green-600" size={16} />
                      <span className="text-gray-700">Greater Auckland Region</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Common questions about our services and processes
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {allFaqs.map((faq) => (
              <AccordionItem
                key={faq.id}
                value={`faq-${faq.id}`}
                className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden"
              >
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-gray-100 text-left">
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4">
                  <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default Contact;
